import { useMemo } from 'react';
import { TreeNode } from '@/types/tree';
import { usePrivileges } from '@/hooks/usePrivileges';
import { useDialogStore } from '@/stores/dialogStore';
import { useTree } from '@/hooks/useTree';
import { useTreeStore } from '@/stores/treeStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import {
  getContextMenuActions,
  getAddMenuActions,
  executeAction,
  IActionHandlerDependencies,
} from '@/types/tree/actions';
import { IBackend } from '@/api/BackendApi';

/**
 * Custom hook that builds action dependencies and returns an executor function.
 * The executor takes the node as a parameter, avoiding stale closure issues.
 */
export const useActionExecutor = () => {
  const {
    showAddToInventoryDialog,
    showCombineStartingMaterialsDialog,
    showMultiSampleGrowthDialog,
    showAddSimulationDatasetDialog,
    showAddTheoryActivityDialog,
    showCommunicationDialog,
    showAddShippingDialog,
    showCharacterizationDialog,
    showProcessingDialog,
    showSplitDialog,
    showStorageDialog,
    showProjectActivityDialog,
    showCreatePublicationDialog,
    showTransferActivityDialog,
    showDeleteDialog,
    showSubmitDialog,
    showResetDialog,
    showSampleSelectionDialog,
    showTheoryActivitySelectionDialog,
    showProjectSelectionDialog,
    showConfirmDialog,
    showDoiDialog,
    showDownloadFolderDialog,
  } = useDialogStore();
  const {
    createNode,
    deleteNode,
    loadChildren,
    getTypedData,
    reloadNode,
    getNodeData,
    clearChildrenLoaded,
    invalidateChildrenCache,
    hasModifiedDataInSubtree,
  } = useTree();
  const expandNode = useTreeStore((state) => state.expandNode);
  const { showSnackbar } = useSnackbar();

  // Build action dependencies once - these are all stable references
  const actionDeps = useMemo(() => {
    const setLoading = useTreeStore.getState().setLoading;
    const markLoading = (dataId: string, flag: boolean) => setLoading(`loading-${dataId}`, flag);

    return {
      showAddToInventoryDialog,
      showCombineStartingMaterialsDialog,
      showMultiSampleGrowthDialog,
      showAddSimulationDatasetDialog,
      showAddTheoryActivityDialog,
      showCommunicationDialog,
      showAddShippingDialog,
      showCharacterizationDialog,
      showProcessingDialog,
      showSplitDialog,
      showStorageDialog,
      showProjectActivityDialog,
      showCreatePublicationDialog,
      showTransferActivityDialog,
      showDeleteDialog,
      showSubmitDialog,
      showResetDialog,
      showSampleSelectionDialog,
      showTheoryActivitySelectionDialog,
      showProjectSelectionDialog,
      showConfirmDialog,
      showDoiDialog,
      showDownloadFolderDialog,
      createNode: (type: any, parentId: string, data: any) => createNode(type, parentId, data),
      deleteNode,
      markLoading,
      reloadNode,
      loadChildren,
      clearChildrenLoaded,
      invalidateChildrenCache,
      expandNode,
      getTypedData,
      getNodeData,
      hasModifiedDataInSubtree,
      showSnackbar,
    };
  }, [
    showAddToInventoryDialog,
    showCombineStartingMaterialsDialog,
    showMultiSampleGrowthDialog,
    showAddSimulationDatasetDialog,
    showAddTheoryActivityDialog,
    showCommunicationDialog,
    showAddShippingDialog,
    showCharacterizationDialog,
    showProcessingDialog,
    showSplitDialog,
    showStorageDialog,
    showProjectActivityDialog,
    showCreatePublicationDialog,
    showTransferActivityDialog,
    showDeleteDialog,
    showSubmitDialog,
    showResetDialog,
    showSampleSelectionDialog,
    showTheoryActivitySelectionDialog,
    showConfirmDialog,
    showDoiDialog,
    showDownloadFolderDialog,
    createNode,
    deleteNode,
    reloadNode,
    loadChildren,
    clearChildrenLoaded,
    invalidateChildrenCache,
    expandNode,
    getTypedData,
    getNodeData,
    hasModifiedDataInSubtree,
    showSnackbar,
    showProjectSelectionDialog,
  ]) as Omit<IActionHandlerDependencies, 'onClose'>;

  // Return executor function that takes node as parameter
  return useMemo(
    () => async (node: TreeNode, actionId: string, backend: IBackend, onClose?: () => void) => {
      return executeAction(actionId, node, backend, { ...actionDeps, onClose });
    },
    [actionDeps]
  );
};

/**
 * Custom hook for accessing node actions with injected dependencies.
 * Use this when you need action lists for a specific node.
 * For executing actions, consider using useActionExecutor() instead.
 */
export const useNodeActions = (node: TreeNode | undefined) => {
  const { isAuthorized } = usePrivileges();
  const { getTypedData, getNodeData } = useTree();
  const executeActionForNode = useActionExecutor();

  const contextMenuActions = useMemo(() => {
    if (!node) return [];
    return getContextMenuActions(node.type, node, isAuthorized, getTypedData, getNodeData);
  }, [node, isAuthorized, getTypedData, getNodeData]);

  const addMenuActions = useMemo(() => {
    if (!node) return [];
    return getAddMenuActions(node.type, node, isAuthorized, getTypedData, getNodeData);
  }, [node, isAuthorized, getTypedData, getNodeData]);

  // Wrapper that uses the node from the hook's closure for backwards compatibility
  const executeNodeAction = async (actionId: string, backend: IBackend, onClose?: () => void) => {
    if (!node) {
      return Promise.resolve();
    }
    return executeActionForNode(node, actionId, backend, onClose);
  };

  return {
    contextMenuActions,
    addMenuActions,
    executeNodeAction,
  };
};
